import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/common";
import { FiPlus, FiX, FiUser } from "react-icons/fi";
import type { ApplicantData, PreferredContactMethod } from "@/types/fact-finder";
import { useFactFinderConfig } from "@/contexts/FactFinderConfigContext";

interface ApplicantsSectionProps {
  applicant1: ApplicantData | null;
  applicant2: ApplicantData | null;
  onSave: (applicant1: ApplicantData, applicant2: ApplicantData | null) => Promise<void>;
  onNext: () => void;
}

const defaultApplicant: ApplicantData = {
  personal: {
    // Basic mode fields
    title: "",
    firstName: "",
    middleNames: "",
    surname: "",
    dateOfBirth: "",
    gender: "",
    preferredContactMethod: "mobile",
    homePhone: "",
    mobile: "",
    workPhone: "",
    email: "",
    countryOfResidence: "Australia",
    isSelfEmployed: false,
    // Advanced mode fields
    isFirstHomeBuyer: false,
    isPermanentResident: true,
    maidenName: "",
    driversLicenceNo: "",
    driversLicenceState: "",
    driversLicenceExpiry: "",
    maritalStatus: "",
    numberOfDependants: 0,
    dependantAges: "",
    nearestRelativeName: "",
    nearestRelativeAddress: "",
    nearestRelativeRelationship: "",
    nearestRelativePhone: "",
  },
  currentAddress: {
    residencyType: "renting",
    rentPerWeek: undefined,
    address: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "Australia",
    dateMovedIn: "",
  },
  previousAddresses: [],
};

export function ApplicantsSection({ applicant1, applicant2, onSave, onNext }: ApplicantsSectionProps) {
  const { advancedMode } = useFactFinderConfig();
  const [activeTab, setActiveTab] = useState<1 | 2>(1);
  const [hasApplicant2, setHasApplicant2] = useState(!!applicant2);
  const [formData1, setFormData1] = useState<ApplicantData>(applicant1 || { ...defaultApplicant });
  const [formData2, setFormData2] = useState<ApplicantData>(applicant2 || { ...defaultApplicant, sameAsApplicant1: false });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  const currentData = activeTab === 1 ? formData1 : formData2;
  const setCurrentData = activeTab === 1 ? setFormData1 : setFormData2;

  const updatePersonal = (field: string, value: unknown) => {
    setCurrentData((prev) => ({
      ...prev,
      personal: { ...prev.personal, [field]: value },
    }));
    if (errors[`${activeTab}_${field}`]) {
      setErrors((prev) => ({ ...prev, [`${activeTab}_${field}`]: "" }));
    }
  };

  const updateAddress = (field: string, value: unknown) => {
    setCurrentData((prev) => ({
      ...prev,
      currentAddress: { ...prev.currentAddress, [field]: value },
    }));
  };

  const addApplicant2 = () => {
    setHasApplicant2(true);
    setActiveTab(2);
  };

  const removeApplicant2 = () => {
    setHasApplicant2(false);
    setFormData2({ ...defaultApplicant, sameAsApplicant1: false });
    setActiveTab(1);
    setShowRemoveDialog(false);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic mode validation (minimal fields)
    if (!formData1.personal.firstName.trim()) newErrors["1_firstName"] = "Required";
    if (!formData1.personal.surname.trim()) newErrors["1_surname"] = "Required";
    if (!formData1.personal.dateOfBirth) newErrors["1_dateOfBirth"] = "Required";
    if (!formData1.personal.mobile.trim()) newErrors["1_mobile"] = "Required";
    if (!formData1.personal.email.trim()) newErrors["1_email"] = "Required";

    // Advanced mode validation (additional fields)
    if (advancedMode) {
      if (!formData1.currentAddress.address.trim()) newErrors["1_address"] = "Required";
      if (!formData1.currentAddress.suburb.trim()) newErrors["1_suburb"] = "Required";
      if (!formData1.currentAddress.state) newErrors["1_state"] = "Required";
      if (!formData1.currentAddress.postcode.trim()) newErrors["1_postcode"] = "Required";

      // Validate Applicant 2 if present (only in advanced mode)
      if (hasApplicant2) {
        if (!formData2.personal.firstName.trim()) newErrors["2_firstName"] = "Required";
        if (!formData2.personal.surname.trim()) newErrors["2_surname"] = "Required";
        if (!formData2.personal.dateOfBirth) newErrors["2_dateOfBirth"] = "Required";
        if (!formData2.personal.mobile.trim()) newErrors["2_mobile"] = "Required";
        if (!formData2.personal.email.trim()) newErrors["2_email"] = "Required";

        if (!formData2.sameAsApplicant1) {
          if (!formData2.currentAddress.address.trim()) newErrors["2_address"] = "Required";
          if (!formData2.currentAddress.suburb.trim()) newErrors["2_suburb"] = "Required";
          if (!formData2.currentAddress.state) newErrors["2_state"] = "Required";
          if (!formData2.currentAddress.postcode.trim()) newErrors["2_postcode"] = "Required";
        }
      }
    }

    setErrors(newErrors);

    // Switch to tab with errors
    const hasErrors1 = Object.keys(newErrors).some((k) => k.startsWith("1_"));
    const hasErrors2 = Object.keys(newErrors).some((k) => k.startsWith("2_"));
    if (hasErrors1) setActiveTab(1);
    else if (hasErrors2) setActiveTab(2);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      await onSave(formData1, hasApplicant2 ? formData2 : null);
      onNext();
    } finally {
      setIsSaving(false);
    }
  };

  const getErrorClass = (field: string) => {
    return errors[`${activeTab}_${field}`] ? "border-destructive" : "";
  };

  const getError = (field: string) => {
    return errors[`${activeTab}_${field}`];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif">
          {advancedMode ? "Applicant Details" : "Personal Details"}
        </h2>
        <p className="text-muted-foreground mt-1">
          {advancedMode
            ? "Provide personal information for all applicants as it appears on ID."
            : "Please confirm/provide your details."}
        </p>
      </div>

      {/* Tabs - Only show in advanced mode */}
      {advancedMode && (
        <div className="flex items-center gap-2 border-b">
          <button
            onClick={() => setActiveTab(1)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 1
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <FiUser className="size-4" />
            Applicant 1
            {Object.keys(errors).some((k) => k.startsWith("1_")) && (
              <span className="w-2 h-2 bg-destructive rounded-full" />
            )}
          </button>

          {hasApplicant2 ? (
            <button
              onClick={() => setActiveTab(2)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 2
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <FiUser className="size-4" />
              Applicant 2
              {Object.keys(errors).some((k) => k.startsWith("2_")) && (
                <span className="w-2 h-2 bg-destructive rounded-full" />
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRemoveDialog(true);
                }}
                className="ml-1 p-0.5 hover:bg-muted rounded"
                title="Remove Applicant 2"
              >
                <FiX className="size-3" />
              </button>
            </button>
          ) : (
            <button
              onClick={addApplicant2}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border-b-2 border-transparent transition-colors"
            >
              <FiPlus className="size-4" />
              Add Applicant
            </button>
          )}
        </div>
      )}

      {/* Same Address Checkbox (only for Applicant 2 in advanced mode) */}
      {advancedMode && activeTab === 2 && (
        <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
          <input
            type="checkbox"
            id="sameAsApplicant1"
            checked={formData2.sameAsApplicant1}
            onChange={(e) =>
              setFormData2((prev) => ({ ...prev, sameAsApplicant1: e.target.checked }))
            }
            className="w-5 h-5 rounded border-gray-300"
          />
          <label htmlFor="sameAsApplicant1" className="text-sm font-medium">
            Same address as Applicant 1
          </label>
        </div>
      )}

      {/* First Home Buyer & Residency - Only in advanced mode */}
      {advancedMode && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
            <input
              type="checkbox"
              id="firstHomeBuyer"
              checked={currentData.personal.isFirstHomeBuyer}
              onChange={(e) => updatePersonal("isFirstHomeBuyer", e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <label htmlFor="firstHomeBuyer" className="text-sm font-medium">
              First Home Buyer?
            </label>
          </div>
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
            <input
              type="checkbox"
              id="permanentResident"
              checked={currentData.personal.isPermanentResident}
              onChange={(e) => updatePersonal("isPermanentResident", e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <label htmlFor="permanentResident" className="text-sm font-medium">
              Australian Permanent Resident / Citizen?
            </label>
          </div>
        </div>
      )}

      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="font-medium">Personal Information</h3>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Title</label>
            <Select
              value={currentData.personal.title}
              onValueChange={(v) => updatePersonal("title", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Mr">Mr</SelectItem>
                <SelectItem value="Mrs">Mrs</SelectItem>
                <SelectItem value="Ms">Ms</SelectItem>
                <SelectItem value="Miss">Miss</SelectItem>
                <SelectItem value="Dr">Dr</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              First Name <span className="text-destructive">*</span>
            </label>
            <Input
              value={currentData.personal.firstName}
              onChange={(e) => updatePersonal("firstName", e.target.value)}
              className={getErrorClass("firstName")}
            />
            {getError("firstName") && (
              <p className="text-xs text-destructive">{getError("firstName")}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Middle Name(s)</label>
            <Input
              value={currentData.personal.middleNames}
              onChange={(e) => updatePersonal("middleNames", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Surname <span className="text-destructive">*</span>
            </label>
            <Input
              value={currentData.personal.surname}
              onChange={(e) => updatePersonal("surname", e.target.value)}
              className={getErrorClass("surname")}
            />
            {getError("surname") && (
              <p className="text-xs text-destructive">{getError("surname")}</p>
            )}
          </div>
        </div>

        <div className={`grid gap-4 ${advancedMode ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Date of Birth <span className="text-destructive">*</span>
            </label>
            <Input
              type="date"
              value={currentData.personal.dateOfBirth}
              onChange={(e) => updatePersonal("dateOfBirth", e.target.value)}
              className={getErrorClass("dateOfBirth")}
            />
            {getError("dateOfBirth") && (
              <p className="text-xs text-destructive">{getError("dateOfBirth")}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Gender</label>
            <Select
              value={currentData.personal.gender}
              onValueChange={(v) => updatePersonal("gender", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
                <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Marital Status - Only in advanced mode */}
          {advancedMode && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Marital Status</label>
              <Select
                value={currentData.personal.maritalStatus}
                onValueChange={(v) => updatePersonal("maritalStatus", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Single">Single</SelectItem>
                  <SelectItem value="Married">Married</SelectItem>
                  <SelectItem value="De Facto">De Facto</SelectItem>
                  <SelectItem value="Divorced">Divorced</SelectItem>
                  <SelectItem value="Widowed">Widowed</SelectItem>
                  <SelectItem value="Separated">Separated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Dependants - Only in advanced mode */}
        {advancedMode && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Number of Dependants</label>
              <Input
                type="number"
                min={0}
                value={currentData.personal.numberOfDependants}
                onChange={(e) => updatePersonal("numberOfDependants", parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Dependant Ages (if any)</label>
              <Input
                value={currentData.personal.dependantAges}
                onChange={(e) => updatePersonal("dependantAges", e.target.value)}
                placeholder="e.g. 5, 8, 12"
              />
            </div>
          </div>
        )}
      </div>

      {/* Contact Details */}
      <div className="space-y-4">
        <h3 className="font-medium">Contact Details</h3>
        {!advancedMode && (
          <p className="text-sm text-muted-foreground">
            The following details may be used to contact you.
          </p>
        )}

        {/* Preferred Contact Method - Basic mode */}
        {!advancedMode && (
          <div className="space-y-2">
            <label className="text-sm font-medium">What is your preferred contact method?</label>
            <Select
              value={currentData.personal.preferredContactMethod}
              onValueChange={(v) => updatePersonal("preferredContactMethod", v as PreferredContactMethod)}
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="mobile">Mobile Phone</SelectItem>
                <SelectItem value="home_phone">Home Phone</SelectItem>
                <SelectItem value="work_phone">Work Phone</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Home Phone</label>
            <Input
              type="tel"
              value={currentData.personal.homePhone}
              onChange={(e) => updatePersonal("homePhone", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Mobile Phone <span className="text-destructive">*</span>
            </label>
            <Input
              type="tel"
              value={currentData.personal.mobile}
              onChange={(e) => updatePersonal("mobile", e.target.value)}
              className={getErrorClass("mobile")}
            />
            {getError("mobile") && (
              <p className="text-xs text-destructive">{getError("mobile")}</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Work Phone</label>
            <Input
              type="tel"
              value={currentData.personal.workPhone}
              onChange={(e) => updatePersonal("workPhone", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Email Address <span className="text-destructive">*</span>
            </label>
            <Input
              type="email"
              value={currentData.personal.email}
              onChange={(e) => updatePersonal("email", e.target.value)}
              className={getErrorClass("email")}
            />
            {getError("email") && (
              <p className="text-xs text-destructive">{getError("email")}</p>
            )}
          </div>
        </div>

        {/* Country of Residence - Basic mode */}
        {!advancedMode && (
          <div className="space-y-2 max-w-xs">
            <label className="text-sm font-medium">Country of Residence</label>
            <Select
              value={currentData.personal.countryOfResidence || "Australia"}
              onValueChange={(v) => updatePersonal("countryOfResidence", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Australia">Australia</SelectItem>
                <SelectItem value="New Zealand">New Zealand</SelectItem>
                <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                <SelectItem value="United States">United States</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Self-Employed - Basic mode */}
      {!advancedMode && (
        <div className="space-y-4">
          <h3 className="font-medium">Employment Status</h3>
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg max-w-md">
            <input
              type="checkbox"
              id="selfEmployed"
              checked={currentData.personal.isSelfEmployed}
              onChange={(e) => updatePersonal("isSelfEmployed", e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <label htmlFor="selfEmployed" className="text-sm font-medium">
              Are you self-employed?
            </label>
          </div>
        </div>
      )}

      {/* Driver's Licence - Only in advanced mode */}
      {advancedMode && (
        <div className="space-y-4">
          <h3 className="font-medium">Driver's Licence</h3>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Licence Number</label>
              <Input
                value={currentData.personal.driversLicenceNo}
                onChange={(e) => updatePersonal("driversLicenceNo", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">State Issued</label>
              <Select
                value={currentData.personal.driversLicenceState}
                onValueChange={(v) => updatePersonal("driversLicenceState", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Expiry Date</label>
              <Input
                type="date"
                value={currentData.personal.driversLicenceExpiry}
                onChange={(e) => updatePersonal("driversLicenceExpiry", e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Current Address - Only in advanced mode, hide for Applicant 2 if same as Applicant 1 */}
      {advancedMode && !(activeTab === 2 && formData2.sameAsApplicant1) && (
        <div className="space-y-4">
          <h3 className="font-medium">Current Address</h3>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(["own_home", "mortgaged", "renting", "boarding", "other"] as const).map((type) => (
              <label
                key={type}
                className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors text-sm ${
                  currentData.currentAddress.residencyType === type
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <input
                  type="radio"
                  name={`residencyType-${activeTab}`}
                  checked={currentData.currentAddress.residencyType === type}
                  onChange={() => updateAddress("residencyType", type)}
                  className="w-4 h-4"
                />
                <span className="capitalize">{type.replace("_", " ")}</span>
              </label>
            ))}
          </div>

          {currentData.currentAddress.residencyType === "renting" && (
            <div className="space-y-2 max-w-xs">
              <label className="text-sm font-medium">Rent per week ($)</label>
              <Input
                type="number"
                value={currentData.currentAddress.rentPerWeek || ""}
                onChange={(e) => updateAddress("rentPerWeek", parseFloat(e.target.value) || undefined)}
                placeholder="0"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">
              Street Address <span className="text-destructive">*</span>
            </label>
            <Input
              value={currentData.currentAddress.address}
              onChange={(e) => updateAddress("address", e.target.value)}
              placeholder="123 Main Street"
              className={getErrorClass("address")}
            />
            {getError("address") && (
              <p className="text-xs text-destructive">{getError("address")}</p>
            )}
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Suburb <span className="text-destructive">*</span>
              </label>
              <Input
                value={currentData.currentAddress.suburb}
                onChange={(e) => updateAddress("suburb", e.target.value)}
                className={getErrorClass("suburb")}
              />
              {getError("suburb") && (
                <p className="text-xs text-destructive">{getError("suburb")}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                State <span className="text-destructive">*</span>
              </label>
              <Select
                value={currentData.currentAddress.state}
                onValueChange={(v) => updateAddress("state", v)}
              >
                <SelectTrigger className={getErrorClass("state")}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getError("state") && (
                <p className="text-xs text-destructive">{getError("state")}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Postcode <span className="text-destructive">*</span>
              </label>
              <Input
                value={currentData.currentAddress.postcode}
                onChange={(e) => updateAddress("postcode", e.target.value)}
                maxLength={4}
                className={getErrorClass("postcode")}
              />
              {getError("postcode") && (
                <p className="text-xs text-destructive">{getError("postcode")}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Date Moved In</label>
              <Input
                type="date"
                value={currentData.currentAddress.dateMovedIn}
                onChange={(e) => updateAddress("dateMovedIn", e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Nearest Relative - Only in advanced mode, only for Applicant 1 */}
      {advancedMode && activeTab === 1 && (
        <div className="space-y-4">
          <h3 className="font-medium">Nearest Relative / Friend (not living with you)</h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Full Name</label>
              <Input
                value={currentData.personal.nearestRelativeName}
                onChange={(e) => updatePersonal("nearestRelativeName", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Relationship</label>
              <Input
                value={currentData.personal.nearestRelativeRelationship}
                onChange={(e) => updatePersonal("nearestRelativeRelationship", e.target.value)}
                placeholder="e.g. Parent, Sibling, Friend"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Address</label>
              <Input
                value={currentData.personal.nearestRelativeAddress}
                onChange={(e) => updatePersonal("nearestRelativeAddress", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Phone Number</label>
              <Input
                type="tel"
                value={currentData.personal.nearestRelativePhone}
                onChange={(e) => updatePersonal("nearestRelativePhone", e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={isSaving} size="lg">
          {isSaving ? "Saving..." : "Save & Continue"}
        </Button>
      </div>

      {/* Remove Applicant 2 Confirmation */}
      <ConfirmDialog
        open={showRemoveDialog}
        onOpenChange={setShowRemoveDialog}
        title="Remove Applicant 2?"
        description="All information entered for Applicant 2 will be cleared. This action cannot be undone."
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={removeApplicant2}
      />
    </div>
  );
}
